import { Col, Form, Input, Row, Select } from 'antd'
import React, { useEffect, useState } from 'react'
import ReactQuill from 'react-quill'
import './styles/editor.scss'
import ImageUploaderCont from './ImageUploaderCont'
import { webContentStore } from 'state/setting/webContent'
import {
  useAddSlideToWeb,
  useUpdateSlideContent,
} from 'api/Settings/WebContent'
import EventModal from '../Modals/EventModal'
import { Photo } from 'types/globalTypes'
import UploadSingleImage from 'components/global/UploadSingleImage'
import { infoMessage } from 'api/global'

const Editor: React.FC = () => {
  const [form] = Form.useForm()
  const currentType = Form.useWatch('type', form)
  const { slides, currentSlide, updateSlideContent } = webContentStore()
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [eventModalOpen, setEventModalOpen] = useState(false)

  const [imagedocId, setImagedocId] = useState<string | null>(null)
  const [imagephoto, setImagephoto] = useState<any | null>(null)

  const {
    mutate: addSlideToWebMutation,
    isLoading: addSlideToWebMutationLoading,
  } = useAddSlideToWeb()
  const {
    mutate: updateSlideContentMutation,
    isLoading: updateSlideContentMutationLoading,
  } = useUpdateSlideContent()

  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ color: ['#F2994A', '#000'] }],
    ],
  }

  const modules2 = {
    toolbar: [['bold', 'italic', 'underline'], [{ color: ['#000'] }]],
  }

  const formats = ['bold', 'italic', 'underline', 'color']

  const handleFinish = (values: any) => {
    if (
      slides[currentSlide].type === 'image' &&
      !slides[currentSlide].imageLink
    ) {
      infoMessage('Card image is required')
      return
    }

    if (
      slides[currentSlide].type === 'video' &&
      !slides[currentSlide].videoLink
    ) {
      infoMessage('Video link is required')
      return
    }

    if (
      slides[currentSlide].type === 'commonEvent' &&
      !slides[currentSlide].commonEventId
    ) {
      infoMessage('Event ID is required')
      return
    }

    const payload = {
      content: { ...values.content },
      galleryIds: (slides[currentSlide].galleryIds || []).filter(
        g => g && g.imageId,
      ),
      type: slides[currentSlide].type,
      videoLink:
        slides[currentSlide].type === 'video'
          ? slides[currentSlide].videoLink
          : undefined,
      imageLink:
        slides[currentSlide].type === 'image'
          ? slides[currentSlide].imageLink
          : undefined,
      commonEventId:
        slides[currentSlide].type === 'commonEvent'
          ? slides[currentSlide].commonEventId
          : undefined,
    }

    if ((slides[currentSlide]?.id as string).includes('local-')) {
      addSlideToWebMutation(payload as any, {
        onSuccess: data => {
          updateSlideContent(slides[currentSlide].id, {
            ...slides[currentSlide],
            id: data._id,
          })
        },
      })
    } else {
      updateSlideContentMutation({
        payload: payload as any,
        slideId: slides[currentSlide].id as string,
      })
    }
  }

  useEffect(() => {
    if (slides?.[currentSlide]?.content) {
      form.setFieldsValue({
        content: {
          heading: slides[currentSlide].content.heading || '',
          description: slides[currentSlide].content.description || '',
          buttonText: slides[currentSlide].content.buttonText || '',
          buttonLink: slides[currentSlide].content.buttonLink || '',
        },
        type: slides[currentSlide].type || 'none',
        videoLink: slides[currentSlide].videoLink || undefined,
        imageLink: slides[currentSlide].imageLink || undefined,
        commonEventId: slides[currentSlide].commonEventId || undefined,
      })
      setSelectedEvent(slides[currentSlide].event)
    } else {
      form.setFieldsValue({
        content: {
          heading: '',
          description: '',
          buttonText: '',
          buttonLink: '',
        },
        type: 'none',
        videoLink: undefined,
        imageLink: undefined,
        commonEventId: undefined,
      })
    }
  }, [slides, currentSlide, form])

  useEffect(() => {
    if (imagephoto) {
      updateSlideContent(slides[currentSlide].id, {
        ...slides[currentSlide],
        imageLink: imagephoto.original,
      })
    }
  }, [imagephoto])

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onValuesChange={(_, allValues) => {
          updateSlideContent(slides[currentSlide]?.id, {
            ...slides[currentSlide]?.content,
            ...allValues.content,
            type: allValues.type,
            videoLink:
              allValues.type === 'video' ? allValues.videoLink : undefined,
          })
        }}
      >
        <>
          <Row>
            <Col>
              <label className="input-label">Header</label>
            </Col>
            <Col>
              <Form.Item className="form-control" name={['content', 'heading']}>
                <ReactQuill
                  theme="snow"
                  modules={modules}
                  formats={formats}
                  placeholder="FALL SOCCER IS ALMOST HERE"
                  className="header-field"
                  onChange={value => {
                    form.setFieldsValue({ content: { heading: value } })
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col>
              <label className="input-label">Support text</label>
            </Col>
            <Col>
              <Form.Item
                className="form-control"
                name={['content', 'description']}
              >
                <ReactQuill
                  theme="snow"
                  modules={modules2}
                  formats={formats}
                  placeholder="FALL SOCCER IS ALMOST HERE"
                  className="description-field"
                  onChange={value => {
                    form.setFieldsValue({ content: { description: value } })
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col>
              <label className="input-label">Call to action</label>
            </Col>
            <Col>
              <Form.Item
                className="form-control"
                name={['content', 'buttonText']}
              >
                <Input
                  placeholder="ex. Learn more"
                  className="form-control-field"
                  onChange={e => {
                    form.setFieldsValue({
                      content: { buttonText: e.target.value },
                    })
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col>
              <label className="input-label">Link</label>
            </Col>
            <Col>
              <Form.Item
                className="form-control"
                name={['content', 'buttonLink']}
              >
                <Input
                  placeholder="ex: https://www.google.com"
                  className="form-control-field"
                  onChange={e => {
                    form.setFieldsValue({
                      content: { buttonLink: e.target.value },
                    })
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col>
              <h4 className="text-16 w-600">Iamge 0/9</h4>
              <p className="text-14 w-400 text-gray">
                Select image position and drag and drop or click to upload from
                computer
              </p>
            </Col>
            <div className="mt-12px">
              <ImageUploaderCont />
            </div>
          </Row>

          <Row className="mt-16px">
            <Col>
              <h4 className="text-16 w-600">Highlight Options</h4>
              <p className="text-14 w-400 text-gray">
                Add an image or select an event to highlight on your layout.
              </p>
            </Col>
            {/* <div className="mt-12px flex items-center gap-12px">
              <Checkbox
                checked={highlightChecked}
                onChange={e => {
                  setHighlightChecked(e.target.checked)
                }}
              />
              <span className="ml-12px">Highlight an event or media item</span>
            </div> */}
          </Row>

          <Row className="mt-16px">
            <Col span={24}>
              <Form.Item name="type" label="Type">
                <Select
                  className="h-50px select-field"
                  placeholder="Select content type"
                  options={[
                    { value: 'none', label: 'None' },
                    { value: 'video', label: 'Video' },
                    { value: 'image', label: 'Image' },
                    { value: 'commonEvent', label: 'Event' },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          {currentType === 'commonEvent' && (
            <>
              <Row>
                <Col>
                  <label className="input-label">Event to highlight</label>
                </Col>
                <div
                  onClick={() => {
                    setEventModalOpen(true)
                  }}
                  className={`w-full border-1px radius-10 flex align-center px-12px pointer h-50px  mb-10px pointer ${
                    selectedEvent && 'text-gray'
                  }`}
                >
                  {selectedEvent
                    ? selectedEvent?.name
                    : 'Select an event to feature (optional)'}
                </div>
              </Row>
            </>
          )}

          {currentType === 'video' && (
            <Row>
              <Col span={24}>
                <label className="input-label">Video URL</label>
                <Form.Item className="form-control" name="videoLink">
                  <Input
                    placeholder="ex: https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                    className="form-control-field"
                  />
                </Form.Item>
              </Col>
            </Row>
          )}

          {currentType === 'image' && (
            <Row>
              <Col span={24}>
                <label className="input-label">Upload image</label>
                <UploadSingleImage
                  fileId={imagedocId}
                  setFileId={setImagedocId}
                  file={imagephoto}
                  setFile={setImagephoto}
                  sourceType={'user_profile'}
                />
              </Col>
            </Row>
          )}

          <Row className="mt-20px">
            <button
              className="prim-btn"
              disabled={
                addSlideToWebMutationLoading ||
                updateSlideContentMutationLoading
              }
            >
              {addSlideToWebMutationLoading || updateSlideContentMutationLoading
                ? 'Saving...'
                : 'Save'}
            </button>
          </Row>
        </>
      </Form>

      {eventModalOpen && (
        <EventModal
          modalOpen={eventModalOpen}
          setModalOpen={setEventModalOpen}
          selectedEvent={selectedEvent}
          setSelectedEvent={setSelectedEvent}
        />
      )}
    </>
  )
}

export default Editor
